import React, { useState, useCallback } from 'react';
import {
  Form, Grid, Divider, Dropdown, Input, Label, Icon, Button,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FakeLink } from 'components/lib/UI';
import RichText from 'components/lib/Editors/RichText';
import toast from 'react-hot-toast';
import util from 'utils/utils';

import { Draggable } from 'react-beautiful-dnd';

const StyledSection = styled.div`
  padding: 20px;
  border-radius: 15px;
  background-color: #fff;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.columnFlex ? 'column-reverse' : 'row')};
  justify-content: ${(props) => (props.columnFlex ? 'center' : 'space-between')};
  align-items: center;
`;

const InfoTextHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const RowFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  > .ui.input {
    margin-right: 70px;
  }
`;

const InfoTextInfo = styled.div`
  display: flex;
  flex-direction: column;
  > h5 {
    margin-bottom: 5px;
  }
  > span {
    margin-bottom: 15px;
  }
`;

const SortTypes = {
  AZ: 'A-Z',
  ZA: 'Z-A',
};

const NewFieldIcon = styled(Icon)`
  margin-left: 15px;
  margin-top: 3px;
  margin-right: 12px;
`;

const SortOptionButtons = ({ fieldIndex, onSort }) => (
  <Button.Group>
    <Button
      content='Sort options A-Z'
      icon='sort alphabet down'
      onClick={() => onSort(fieldIndex, SortTypes.AZ)}
    />
    <Button
      content='Sort options Z-A'
      icon='sort alphabet up'
      onClick={() => onSort(fieldIndex, SortTypes.ZA)}
    />

  </Button.Group>
);

const getFieldWidth = (field) => {
  if (field.type === 'row') return 16;
  if (field.width === 'sixteen') return 16;
  if (field.width) {
    if (field.width === 'four' || field.width === 4) {
      return 4;
    }
    return field.width;
  }
  return 16;
};

const isWideField = (field) => {
  const fieldWidth = getFieldWidth(field);
  if (typeof fieldWidth === 'number') {
    return fieldWidth >= 12;
  }
  return !!(((fieldWidth === 'twelve') || (fieldWidth === 'sixteen')));
};

const FormatField = (props) => {
  const {
    field = {},
    index,
    t,
    fieldType,
    templateFieldTypes,
    fields = [],
    editFieldOptions,
    moveField,
    deleteField,
    editFieldTitle,
    editFieldDescription,
    editFieldLimit,
    editFieldTextbox,
    editFieldSlider,
    editFieldSliderMin,
    editFieldSliderMax,
    editFieldMaxImages,
    editFieldRequired,
    editInfoTextContent,
    removeCharacterLimit,
    addNewFieldBefore,
    addNewFieldAfter,
    challengeId,
    setRef,
  } = props;
  const [newDropdown, setNewDropdown] = useState('');
  const [fieldVisible, setFieldVisible] = useState(true);
  const [newCheckbox, setNewCheckbox] = useState('');

  const addCheckbox = useCallback((atIndex) => {
    const checkboxes = Object.assign([], field.options);
    if (checkboxes.some((entry) => entry === newCheckbox)) {
      toast.error('You have already added this option, please do not enter duplicates.');
      return;
    }
    checkboxes.push(newCheckbox);
    editFieldOptions(atIndex, checkboxes);
    setNewCheckbox('');
  }, [editFieldOptions, field, newCheckbox]);

  const removeCheckbox = useCallback((atIndex, i) => {
    const checkboxes = Object.assign([], field.options);
    checkboxes.splice(i, 1);
    editFieldOptions(atIndex, checkboxes);
  }, [editFieldOptions, field]);

  const addDropdown = useCallback((atIndex) => {
    const dropdowns = Object.assign([], field.options);
    if (dropdowns.some((entry) => entry === newDropdown)) {
      toast.error('You have already added this option, please do not enter duplicates.');
      return;
    }
    dropdowns.push(newDropdown);
    editFieldOptions(atIndex, dropdowns);
    setNewDropdown('');
  }, [newDropdown, editFieldOptions, field]);

  const removeDropdown = useCallback((atIndex, i) => {
    const dropdowns = Object.assign([], field.options);
    dropdowns.splice(i, 1);
    editFieldOptions(atIndex, dropdowns);
  }, [field, editFieldOptions]);

  const sortOptions = useCallback((atIndex, direction = SortTypes.AZ) => {
    const sortedFieldOptions = [...field.options].sort((a, b) => (direction === SortTypes.AZ ? a - b : b - a));
    sortedFieldOptions.sort((a, b) => {
      const valueA = a.toLowerCase(); const
        valueB = b.toLowerCase();
      if (valueA < valueB) return direction === SortTypes.AZ ? -1 : 1;
      if (valueA > valueB) return direction === SortTypes.AZ ? 1 : -1;
      return 0;
    });
    editFieldOptions(atIndex, sortedFieldOptions);
  }, [field, editFieldOptions]);

  const NewFieldSelector = useCallback((text, propType) => (
    <Dropdown.Item>
      <NewFieldIcon name='add circle' />
      <Dropdown text={text} className='link item' icon={null} pointing='left' direction='left' on='hover'>
        <Dropdown.Menu>
          {templateFieldTypes.map((type) => <Dropdown.Item key={type.key} data-tooltip={type.desc} icon={type.icon} content={type.name} onClick={() => {
            if (propType === 'before') {
              addNewFieldBefore(type.key, index);
            } else {
              addNewFieldAfter(type.key, index);
            }
          }} />)}
        </Dropdown.Menu>
      </Dropdown>
    </Dropdown.Item>
  ), [templateFieldTypes, index, addNewFieldBefore, addNewFieldAfter]);

  if (!field.id) return null;
  return (
    <Draggable key={field.id} draggableId={`field-${field.id}`} index={index}>
      {(provided) => (
        <Grid.Column computer={getFieldWidth(field)} style={{ marginBottom: 0, marginTop: 0 }} title={`${field.type} ${field.title}`}>
          <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
            <StyledSection ref={(ref) => setRef(ref, field.id)} id={field.id}>
              {field.type === 'infotext'
                && (
                  <div>
                    <InfoTextHeader>
                      <div
                        style={{
                          borderRight: '1px solid lightgray',
                          paddingRight: 10,
                          marginRight: 10,
                        }}
                      >
                        <FakeLink
                          withBorder
                          style={{ color: '#000' }}
                          onClick={() => setFieldVisible((prevVisible) => !prevVisible)}
                        >
                          <Icon name={fieldVisible ? 'chevron up' : 'chevron down'} />
                          {fieldVisible ? 'Minimise' : 'Maximise'}
                        </FakeLink>
                      </div>
                      <Dropdown direction='left' icon={null}
                        trigger={
                          <FakeLink withBorder style={{ color: '#000' }}><Icon name='dropdown' />Field options </FakeLink>
                        }
                      >
                        <Dropdown.Menu>
                          {index > 0
                            && <Dropdown.Item icon='arrow up' content={t('challenge.format.formatField.field.moveUp')} onClick={() => moveField(index, 'up')} />
                          }
                          {index < (fields.length - 1)
                            && <Dropdown.Item icon='arrow down' content={t('challenge.format.formatField.field.moveDown')} onClick={() => moveField(index, 'down')} />
                          }
                          {fields.length > 1
                            && (
                              <Dropdown.Divider />
                            )
                          }
                          <Dropdown.Item icon='trash' content={t('challenge.format.formatField.field.deleteField')} onClick={() => deleteField(index)} />
                        </Dropdown.Menu>
                      </Dropdown>
                    </InfoTextHeader>
                    {fieldVisible
                      ? (
                        <InfoTextInfo>
                          <h5>Information text</h5>
                          <span>Use this field to convey information to the idea creator.  You can use images, videos, links, and more. This field takes no input from the idea creator.</span>
                          <span>This field will only be shown to idea creators whilst they create their ideas.</span>
                          <RichText
                            value={field.content}
                            onChange={(v) => editInfoTextContent(index, v)}
                            forType='challenge'
                            forId={challengeId}
                          />
                        </InfoTextInfo>
                      ) : null
                    }

                  </div>
                )
              }
              {field.type === 'row'
                && (
                  <div>
                    <DropdownContainer>
                      <div />
                      <Dropdown direction='left' icon={null}
                        trigger={
                          <FakeLink withBorder style={{ color: '#000' }}><Icon name='dropdown' />Field options </FakeLink>
                        }
                      >
                        <Dropdown.Menu>
                          {index > 0
                            && <Dropdown.Item icon='arrow up' content={t('challenge.format.formatField.field.moveUp')} onClick={() => moveField(index, 'up')} />
                          }
                          {index < (fields.length - 1)
                            && <Dropdown.Item icon='arrow down' content={t('challenge.format.formatField.field.moveDown')} onClick={() => moveField(index, 'down')} />
                          }
                          <Dropdown.Divider />
                          <Dropdown.Item icon='trash' content={t('challenge.format.formatField.field.deleteField')} onClick={() => deleteField(index)} />
                        </Dropdown.Menu>
                      </Dropdown>
                    </DropdownContainer>
                    <Divider />
                  </div>
                )
              }
              {!(field.type === 'infotext') && !(field.type === 'row')
                && (
                  <div>
                    <DropdownContainer columnFlex={!isWideField(field)}>
                      {fieldVisible ? <div style={{ marginTop: 10 }} /> : (
                        <div style={{ marginTop: isWideField(field) ? 0 : 10 }}><h3 style={{ display: 'inline' }}>{field.title}</h3><h5 style={{ display: 'inline' }}>{templateFieldTypes && ` - ${templateFieldTypes.find((f) => f.key === field.type).name}`}</h5>{field.required && <p style={{
                          color: 'red', fontSize: 11, marginLeft: 5, display: 'inline',
                        }}><i>* required</i></p>}</div>
                      )}
                      <div
                        style={{
                          display: 'flex', flexDirection: getFieldWidth(field) > 4 ? 'row' : 'column', alignItems: getFieldWidth(field) > 4 ? 'center' : 'flex-end', width: getFieldWidth(field) > 4 ? '30%' : '100%', justifyContent: 'flex-end',
                        }}
                      >
                        <div
                          style={getFieldWidth(field) > 4 ? {
                            borderRight: '1px solid lightgray',
                            paddingRight: 10,
                            marginRight: 10,
                          } : {}}
                        >
                          <FakeLink
                            withBorder
                            style={{ color: '#000' }}
                            onClick={() => setFieldVisible((prevVisible) => !prevVisible)}
                          >
                            <Icon name={fieldVisible ? 'chevron up' : 'chevron down'} />
                            {fieldVisible ? 'Minimise' : 'Maximise'}
                          </FakeLink>
                        </div>
                        <Dropdown direction='left' icon={null}
                          trigger={
                            <FakeLink withBorder style={{ color: '#000' }}><Icon name='dropdown' />Field options </FakeLink>
                          }
                        >
                          <Dropdown.Menu>
                            {NewFieldSelector(t('challenge.format.formatField.field.addBefore'), 'before')}
                            {NewFieldSelector(t('challenge.format.formatField.field.addAfter'), 'after')}
                            <Dropdown.Divider />
                            {index > 0
                              && <Dropdown.Item icon='arrow up' content={t('challenge.format.formatField.field.moveUp')} onClick={() => moveField(index, 'up')} />
                            }
                            {index < (fields.length - 1)
                              && <Dropdown.Item icon='arrow down' content={t('challenge.format.formatField.field.moveDown')} onClick={() => moveField(index, 'down')} />
                            }
                            <Dropdown.Divider />
                            <Dropdown.Item icon='trash' content={t('challenge.format.formatField.field.deleteField')} onClick={() => deleteField(index)} />
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </DropdownContainer>
                    {fieldVisible
                      ? (
                        <Form>
                          <Form.Input size='small' fluid
                            label={field.required ? <div style={{ display: 'flex' }}><p style={{ fontSize: '.92857143em', fontWeight: 700, marginBottom: 4 }}>Field title</p><p style={{ color: 'red', fontSize: 11, marginLeft: 3 }}><i>* required</i></p></div> : t('challenge.format.formatField.form.title.title')}
                            icon={fieldType.icon} iconPosition='left'
                            data-tooltip={`${fieldType.name} field`}
                            placeholder={t('challenge.format.formatField.form.title.info')}
                            value={field.title}
                            onChange={(e) => editFieldTitle(index, e.target.value)} />

                          <Form.TextArea size='small' label={t('challenge.format.formatField.form.description.title')}
                            placeholder={t('challenge.format.formatField.form.description.info')}
                            value={field.description}
                            onChange={(e) => editFieldDescription(index, e.target.value)} />

                          {field.characterLimit
                            ? (
                              <>
                                <p>
                                  <b>{t('challenge.format.formatField.form.characterLimit.info')}</b>
                                </p>
                                <Form.Input
                                  size='small'
                                  fluid
                                  disabled
                                  type='number'
                                  value={field.characterLimit}
                                />
                                <div
                                  style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                                >
                                  <Button
                                    size='tiny'
                                    color='red'
                                    content='Remove character limit'
                                    onClick={() => removeCharacterLimit(index)}
                                  />
                                </div>
                              </>

                            ) : null
                          }

                          {(field.type === 'longtext' || field.type === 'richtext' || field.type === 'shorttext' || (field.type === 'slider' && !field.hideTextbox))
                            && <Form.Input
                              size='small'
                              fluid
                              label={`${t('challenge.format.formatField.form.wordLimit.title')} (${t('challenge.format.formatField.form.wordLimit.info')})`}
                              placeholder={t('challenge.format.formatField.form.wordLimit.info')}
                              value={field.wordLimit}
                              onChange={(e) => util.limitNumberInput(e, (numericalInput) => editFieldLimit(index, numericalInput))}
                            />
                          }

                          {field.type === 'slider'
                            && <Form.Checkbox label='Hide justification text box'
                              checked={!!field.hideTextbox}
                              onChange={() => editFieldTextbox(index, !field.hideTextbox)}
                            />
                          }

                          {field.type === 'slider' && field.hideSliders
                            && <div>
                              <Form.Input size='small' fluid
                                label='Enter the minimum and maximum value for the slider'
                                placeholder='Leave blank for a lower limit of 0'
                                type='number'
                                value={field.minslider}
                                onChange={(e) => editFieldSliderMin(index, parseInt(e.target.value, 10))} />

                              <Form.Input size='small'
                                placeholder='Leave blank for an upper limit of 10'
                                value={field.maxslider}
                                type='number'
                                onChange={(e) => editFieldSliderMax(index, parseInt(e.target.value, 10))}
                              />
                              <p></p>
                            </div>
                          }

                          {field.type === 'multiimage'
                            && (
                              <Form.Input
                                size='small'
                                fluid
                                label='Enter the maximum number of images a user can upload to the gallery. Leave it at zero to allow unlimited uploads'
                                type='number'
                                value={field.maxImages}
                                onChange={(e) => editFieldMaxImages(index, parseInt(e.target.value, 10))}
                              />
                            )
                          }

                          {field.type === 'slider'
                            && <Form.Checkbox label='Add custom slider limits'
                              checked={field.hideSliders}
                              onChange={() => editFieldSlider(index, !field.hideSliders)}
                            />
                          }

                          {field.type === 'dropdown'
                            && <div>
                              <label>Add your Drop Down options below</label>
                              <Grid stackable>
                                <Grid.Row computer={12}>
                                  <RowFlex>
                                    <Input
                                      size='small'
                                      style={{ maxWidth: field.width <= 8 ? '60%' : null, marginLeft: '15px' }}
                                      value={newDropdown}
                                      onChange={(e) => setNewDropdown(e.target.value)}
                                      label={{
                                        attatched: 'right', content: 'Add +', onClick: () => addDropdown(index), style: { cursor: 'pointer' },
                                      }}
                                      labelPosition='right'
                                      placeholder='Drop down option'
                                    />
                                    {(field.options?.length || 0) > 1
                                      ? (
                                        <SortOptionButtons
                                          fieldIndex={index}
                                          onSort={sortOptions}
                                        />
                                      ) : null
                                    }
                                  </RowFlex>

                                </Grid.Row>
                                <Grid.Row computer={8}>
                                  {field.options && field.options.map((option, i) => <Label style={{ marginBottom: 5, marginRight: 5, marginLeft: '10px' }} key={option} option>{option} <Icon name='close' onClick={() => removeDropdown(index, i)} /></Label>)}
                                </Grid.Row>
                              </Grid>
                            </div>
                          }

                          {field.type === 'optionMultiple'
                            && <div>
                              <label>Add your checkbox options below</label>
                              <Grid stackable>
                                <Grid.Row computer={12}>
                                  <RowFlex>
                                    <Input
                                      size='small'
                                      style={{ maxWidth: field.width <= 8 ? '60%' : null, marginLeft: '15px' }}
                                      value={newCheckbox}
                                      onChange={(e) => setNewCheckbox(e.target.value)}
                                      label={{
                                        attatched: 'right', content: 'Add +', onClick: () => addCheckbox(index), style: { cursor: 'pointer' },
                                      }}
                                      labelPosition='right'
                                      placeholder='Checkbox option'
                                    />
                                    {(field.options?.length || 0) > 1
                                      ? (
                                        <SortOptionButtons
                                          fieldIndex={index}
                                          onSort={sortOptions}
                                        />
                                      ) : null
                                    }
                                  </RowFlex>

                                </Grid.Row>
                                <Grid.Row computer={8}>
                                  {field.options && field.options.map((option, i) => <Label style={{ marginBottom: 5, marginRight: 5, marginLeft: '10px' }} key={option} option>{option} <Icon name='close' onClick={() => removeCheckbox(index, i)} /></Label>)}
                                </Grid.Row>
                              </Grid>
                            </div>
                          }

                          {
                            (
                              !field.type === 'slider'
                              || !field.hideTextbox
                            ) && (
                              <Form.Checkbox style={{ marginTop: 4 }} label='Set field as required' checked={field.required === true} onChange={() => editFieldRequired(index, field.required ? undefined : true)} />
                            )
                          }

                          {
                            (
                              field.type === 'slider'
                              && field.hideTextbox
                            ) && (
                              <span style={{ color: 'red' }}><i>Slider fields can only be made required if the justification textbox is not hidden.</i></span>
                            )
                          }

                        </Form>
                      ) : null
                    }
                  </div>
                )}
            </StyledSection>
          </div>
        </Grid.Column>
      )}
    </Draggable>
  );
};

export default withTranslation()(FormatField);
